// ========================================
// COMPONENTE FOLDERSETTINGSMODAL
// Gestión de permisos de una carpeta: el creador (o admin) otorga a otros
// usuarios permisos de Ver / Subir / Eliminar. Las subcarpetas los heredan.
// ========================================

import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import config from '../Componentes/config';
import { registrarLog } from './logService';

// Props:
// - folder: carpeta sobre la que se gestionan permisos (objeto con idCarpeta, nombreCarpeta, usuarioCrea)
// - usuarioActual: username del usuario que gestiona (solicitante)
// - onClose: cerrar el modal
const FolderSettingsModal = ({ folder, usuarioActual, onClose }) => {
    const [usuarios, setUsuarios] = useState([]);          // Todos los usuarios del sistema
    const [permisos, setPermisos] = useState({});          // { username: { puedeVer, puedeSubir, puedeEliminar } }
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);                // username en proceso de guardado
    const [search, setSearch] = useState('');

    // Cargar usuarios y permisos actuales
    useEffect(() => {
        let activo = true;
        const cargar = async () => {
            setLoading(true);
            try {
                const [resUsuarios, resPermisos] = await Promise.all([
                    fetch(`${config.API_URL}/getAllUsuarios`),
                    fetch(`${config.API_URL}/carpetas/${folder.idCarpeta}/permisos`)
                ]);
                const listaUsuarios = resUsuarios.ok ? await resUsuarios.json() : [];
                const listaPermisos = resPermisos.ok ? await resPermisos.json() : [];

                if (!activo) return;
                setUsuarios(listaUsuarios);
                const mapa = {};
                listaPermisos.forEach(p => {
                    mapa[p.usuario] = {
                        puedeVer: !!p.puedeVer,
                        puedeSubir: !!p.puedeSubir,
                        puedeEliminar: !!p.puedeEliminar
                    };
                });
                setPermisos(mapa);
            } catch (err) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudieron cargar los permisos',
                    confirmButtonColor: '#d33'
                });
            } finally {
                if (activo) setLoading(false);
            }
        };
        cargar();
        return () => { activo = false; };
    }, [folder.idCarpeta]);

    // Usuarios candidatos: excluir al creador (siempre tiene control total) y aplicar búsqueda
    const usuariosFiltrados = useMemo(() => {
        const termino = search.trim().toLowerCase();
        return usuarios
            .filter(u => u.Usuario !== folder.usuarioCrea)
            .filter(u => {
                if (!termino) return true;
                const nombre = `${u.Nombres || ''} ${u.Apellidos || ''} ${u.Usuario || ''}`.toLowerCase();
                return nombre.includes(termino);
            });
    }, [usuarios, search, folder.usuarioCrea]);

    // Guardar permiso (POST). Si puedeVer queda en false, se revoca (DELETE).
    const guardarPermiso = async (username, nuevo) => {
        setBusy(username);
        try {
            if (!nuevo.puedeVer) {
                // Revocar acceso por completo
                const res = await fetch(
                    `${config.API_URL}/carpetas/${folder.idCarpeta}/permisos/${encodeURIComponent(username)}?solicitante=${encodeURIComponent(usuarioActual)}`,
                    { method: 'DELETE' }
                );
                if (!res.ok) throw new Error('No se pudo revocar el permiso');
                setPermisos(prev => {
                    const copia = { ...prev };
                    delete copia[username];
                    return copia;
                });
            } else {
                const res = await fetch(`${config.API_URL}/carpetas/${folder.idCarpeta}/permisos`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        usuario: username,
                        puedeVer: nuevo.puedeVer,
                        puedeSubir: nuevo.puedeSubir,
                        puedeEliminar: nuevo.puedeEliminar,
                        solicitante: usuarioActual
                    })
                });
                if (!res.ok) {
                    const e = await res.json();
                    throw new Error(e.error || 'No se pudo guardar el permiso');
                }
                setPermisos(prev => ({ ...prev, [username]: nuevo }));
            }
            await registrarLog({
                Usuario: usuarioActual,
                Modulo: 'CarpetaAdministrativa',
                Detalle: `Permisos de "${folder.nombreCarpeta}" para ${username}: ${nuevo.puedeVer ? 'ver' : 'sin acceso'}${nuevo.puedeSubir ? ',subir' : ''}${nuevo.puedeEliminar ? ',eliminar' : ''}`,
                Estado: 'Éxito',
            });
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.message || 'No se pudo actualizar el permiso',
                confirmButtonColor: '#d33'
            });
        } finally {
            setBusy(null);
        }
    };

    // Cambiar un flag de un usuario. "ver" es el acceso base: al activarlo se crea el permiso,
    // al desactivarlo se revocan todos. "subir"/"eliminar" implican "ver".
    const toggleFlag = (username, flag) => {
        const actual = permisos[username] || { puedeVer: false, puedeSubir: false, puedeEliminar: false };
        const nuevo = { ...actual, [flag]: !actual[flag] };
        if (flag === 'puedeVer' && !nuevo.puedeVer) {
            nuevo.puedeSubir = false;
            nuevo.puedeEliminar = false;
        }
        if ((flag === 'puedeSubir' || flag === 'puedeEliminar') && nuevo[flag]) {
            nuevo.puedeVer = true; // subir/eliminar requieren acceso de ver
        }
        guardarPermiso(username, nuevo);
    };

    return (
        <div className="modal-overlay2" onClick={onClose}>
            <div className="modal2" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', width: '90%' }}>
                <div className="modal-header2">
                    <h2 className="modal-title2">Compartir "{folder.nombreCarpeta}"</h2>
                </div>

                <div className="modal-body2">
                    <p style={{ color: '#6c757d', marginBottom: '12px', fontSize: '14px' }}>
                        Otorga acceso a otros usuarios. Las subcarpetas heredan estos permisos.
                    </p>

                    <input
                        type="text"
                        className="form-input"
                        placeholder="Buscar usuario..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ marginBottom: '12px' }}
                    />

                    {loading ? (
                        <div className="loading-spinner">Cargando permisos...</div>
                    ) : (
                        <div className="permisos-lista" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #e3e6f0' }}>
                                        <th style={{ padding: '8px' }}>Usuario</th>
                                        <th style={{ padding: '8px', textAlign: 'center' }}>Ver</th>
                                        <th style={{ padding: '8px', textAlign: 'center' }}>Subir</th>
                                        <th style={{ padding: '8px', textAlign: 'center' }}>Eliminar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usuariosFiltrados.length === 0 && (
                                        <tr><td colSpan={4} style={{ padding: '12px', color: '#6c757d' }}>No hay usuarios.</td></tr>
                                    )}
                                    {usuariosFiltrados.map((u) => {
                                        const p = permisos[u.Usuario] || { puedeVer: false, puedeSubir: false, puedeEliminar: false };
                                        const enProceso = busy === u.Usuario;
                                        return (
                                            <tr key={u.ID} style={{ borderBottom: '1px solid #eee', opacity: enProceso ? 0.5 : 1 }}>
                                                <td style={{ padding: '8px' }}>
                                                    <div style={{ fontWeight: 600 }}>{u.Nombres} {u.Apellidos}</div>
                                                    <div style={{ fontSize: '12px', color: '#6c757d' }}>@{u.Usuario}</div>
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    <input type="checkbox" disabled={enProceso}
                                                        checked={p.puedeVer}
                                                        onChange={() => toggleFlag(u.Usuario, 'puedeVer')} />
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    <input type="checkbox" disabled={enProceso}
                                                        checked={p.puedeSubir}
                                                        onChange={() => toggleFlag(u.Usuario, 'puedeSubir')} />
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    <input type="checkbox" disabled={enProceso}
                                                        checked={p.puedeEliminar}
                                                        onChange={() => toggleFlag(u.Usuario, 'puedeEliminar')} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="modal-actions2">
                    <button type="button" className="btn btn-primary" onClick={onClose}>
                        Listo
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FolderSettingsModal;
